import SelectionButton, { SelectionButtonLabel } from '../../components/selection-button';
import { FC, MouseEvent, useCallback } from 'react';
import OddsChangeCounter from './odds-change-counter';
import { useSportsbookBetslipSelection } from '../hooks/use-sportsbook-betslip';
import { SelectionDto } from '../api/sbapi/sbapi-api-model';
import { Skeleton } from '@comeon/ui';
import useOddsFormat from '../hooks/use-odds-format';
import formatOdds from '../utils/format-odds';
import useOddsChangeStyles from '../hooks/use-odds-change-styles';
import styled, { RuleSet } from 'styled-components';
import normalizeOdds from '../utils/normalize-odds';
import { useTypedTranslationsWithPartialResult } from '../../../translation/use-translations';
import SportsbookTextKeys from '../sportsbook-text-keys';

const StyledSelectionButton = styled(SelectionButton)<{ oddsChangeStyles?: RuleSet }>`
	${({ oddsChangeStyles }) => oddsChangeStyles}
`;

type Props = {
	selection: SelectionDto;
	label?: string;
	className?: string;
	isDisabled?: boolean;
	onClick?: (e: MouseEvent<HTMLButtonElement>, selection: SelectionDto) => void;
	tag: string;
};

/**
 * Sportsbook selection button. Has a connection to the betslip state.
 * @param selection Selection to be displayed.
 * @param label Optional label to be displayed instead of selection's `betslipLine`.
 * @param tag - The tag to determine origin of selection for tracking purposes.
 * @param className Optional class name.
 * @param onClick Optional click handler.
 */
const SportsbookSelectionButton: FC<Props> = ({ selection, label, className, isDisabled, tag, onClick }) => {
	const { translate } = useTypedTranslationsWithPartialResult(SportsbookTextKeys);

	const { isSelectionInBetslip, toggleSelection } = useSportsbookBetslipSelection(selection.id);
	const { oddsFormat } = useOddsFormat();

	const selectionLabel = label ?? selection.betslipLine;
	const normalizedOdds = normalizeOdds(selection.trueOdds);
	const renderOdds = useCallback((odds: number) => formatOdds(odds, oddsFormat), [oddsFormat]);
	const oddsChangeStyles = useOddsChangeStyles(normalizedOdds);

	const handleClick = useCallback(
		(e: MouseEvent<HTMLButtonElement>) => {
			toggleSelection(selection, tag);
			onClick?.(e, selection);
		},
		[toggleSelection, selection, tag, onClick]
	);

	return (
		<StyledSelectionButton
			className={className}
			oddsChangeStyles={!isDisabled ? oddsChangeStyles : undefined}
			data-at="sportsbook-selection-btn"
			isActive={isSelectionInBetslip}
			disabled={isDisabled} // map to button's native property. Styled with :disabled pseudo-class.
			onClick={handleClick}
			aria-label={translate(SportsbookTextKeys.SELECTION_BUTTON_ARIA_LABEL, {
				selection: selection.betslipLine,
				odds: normalizedOdds
			})}
		>
			{selectionLabel && <SelectionButtonLabel>{selectionLabel}</SelectionButtonLabel>}
			<OddsChangeCounter odds={normalizedOdds} renderOdds={renderOdds} />
		</StyledSelectionButton>
	);
};

/**
 * Skeleton for sportsbook selection button. Accept all props from `Skeleton` component.
 */
export const SkeletonSelectionButton = () => <Skeleton height="37px" width="100%" borderRadius="6px" />;

export default SportsbookSelectionButton;
